import { getApiErrorMessage } from "../../../../../utils/commonFunction";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { useDebtCollectionUIContext } from "../DebtCollectionUIContext";
import { useIntl } from "react-intl";
import { DebtCollectionFilterForm } from "./DebtCollectionFilterForm";
import { findFranchise } from "../../../_redux/franchises/franchisesCrud";
import {
  downloadCsvExportResponse,
  exportFranchiseDebtLedger,
  exportFranchiseDebtSummary,
} from "../../../_redux/franchises/franchiseDebtCrud";
import { buildDebtListExportApiQuery } from "../DebtCollectionUIHelpers";
import SnackBarTool from "../../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";

/** Parses a JSON error body from a failed blob export response. */
async function messageFromErrorBlob(blob) {
  try {
    const text = await blob.text();
    const j = JSON.parse(text);
    if (j && typeof j.message === "string" && j.message.trim()) {
      return j.message.trim();
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Debt list toolbar: franchise/status filters, search, and CSV export actions.
 * @returns {JSX.Element} Formik-wrapped filter row and snackbar.
 */
export function DebtCollectionFilter() {
  const intl = useIntl();
  const [franchiseOptions, setFranchiseOptions] = useState([]);
  const [exportBusy, setExportBusy] = useState(null);
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });
  const {
    queryParams,
    setQueryParams,
    showDebtFranchiseFilter,
    scopedFranchiseIdForDebtList,
    isDebtFranchiseOwner,
  } = useDebtCollectionUIContext();

  const prepareFilter = useCallback(
    (queryParams, values) => {
      const newQueryParams = { ...queryParams };
      const franchiseFromForm =
        values.franchiseId != null && String(values.franchiseId).trim() !== ""
          ? String(values.franchiseId).trim()
          : "";
      const franchise_id =
        scopedFranchiseIdForDebtList != null &&
        String(scopedFranchiseIdForDebtList).trim() !== ""
          ? String(scopedFranchiseIdForDebtList).trim()
          : franchiseFromForm;
      newQueryParams.filter = {
        ...queryParams.filter,
        search: values.searchText != null ? String(values.searchText) : "",
        franchise_id,
        status:
          values.status !== "" &&
          values.status != null &&
          String(values.status).trim() !== ""
            ? String(values.status).trim()
            : "",
      };
      return newQueryParams;
    },
    [scopedFranchiseIdForDebtList],
  );

  useEffect(() => {
    if (
      scopedFranchiseIdForDebtList == null ||
      String(scopedFranchiseIdForDebtList).trim() === ""
    ) {
      return undefined;
    }
    const fid = String(scopedFranchiseIdForDebtList).trim();
    setQueryParams((prev) => {
      if (prev.filter?.franchise_id === fid) return prev;
      return {
        ...prev,
        pageNumber: 1,
        filter: { ...prev.filter, franchise_id: fid },
      };
    });
    return undefined;
  }, [scopedFranchiseIdForDebtList, setQueryParams]);

  useEffect(() => {
    if (!showDebtFranchiseFilter) {
      setFranchiseOptions([]);
      return undefined;
    }
    let cancelled = false;
    findFranchise({
      pageNumber: 1,
      pageSize: 500,
      sortField: "id",
      sortOrder: "asc",
      filter: { search: "" },
    })
      .then((res) => {
        if (cancelled) return;
        const raw = Array.isArray(res?.data?.data) ? res.data.data : [];
        setFranchiseOptions(raw.filter((f) => f != null && f.id != null));
      })
      .catch(() => {
        if (!cancelled) setFranchiseOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showDebtFranchiseFilter]);

  const applyFilter = useCallback(
    (values) => {
      setQueryParams((prev) => {
        const next = prepareFilter(prev, values);
        if (isEqual(next, prev)) return prev;
        return { ...next, pageNumber: 1 };
      });
    },
    [setQueryParams, prepareFilter],
  );

  const emptyFilterValues = useMemo(
    () => ({
      searchText: "",
      franchiseId: "",
      status: "",
    }),
    [],
  );

  const exportQueryParams = useMemo(
    () =>
      buildDebtListExportApiQuery(queryParams, {
        scopedFranchiseId: scopedFranchiseIdForDebtList,
        omitFranchiseIdForExport: Boolean(isDebtFranchiseOwner),
      }),
    [queryParams, scopedFranchiseIdForDebtList, isDebtFranchiseOwner],
  );

  const runDebtExport = useCallback(
    (kind, exportFn) => {
      setSnack({ call: false, type: "", message: "" });
      setExportBusy(kind);
      const defaultFilename =
        kind === "ledger"
          ? "franchise-debt-ledger.csv"
          : "franchise-debt-summary.csv";
      exportFn(exportQueryParams)
        .then((res) => {
          if (downloadCsvExportResponse(res, defaultFilename)) {
            setSnack({
              call: true,
              type: "success",
              message: "Your file download has started.",
            });
          } else {
            setSnack({
              call: true,
              type: "error",
              message: "Export did not return a file. Please try again.",
            });
          }
        })
        .catch((error) => {
          const finish = async () => {
            let message = "Something went wrong. Please try again.";
            const data = error?.response?.data;
            if (data instanceof Blob) {
              const fromBlob = await messageFromErrorBlob(data);
              if (fromBlob) message = fromBlob;
            } else {
              message = getApiErrorMessage(error);
            }
            setSnack({ call: true, type: "error", message });
          };
          return finish();
        })
        .finally(() => {
          setExportBusy(null);
        });
    },
    [exportQueryParams],
  );

  const handleExportSummary = useCallback(() => {
    runDebtExport("summary", exportFranchiseDebtSummary);
  }, [runDebtExport]);

  const handleExportLedger = useCallback(() => {
    runDebtExport("ledger", exportFranchiseDebtLedger);
  }, [runDebtExport]);

  return (
    <>
      {snack.call ? <SnackBarTool {...snack} /> : null}
      <Formik
        initialValues={emptyFilterValues}
        onSubmit={(values) => {
          applyFilter(values);
        }}
      >
        {({ values, handleSubmit, handleBlur, setFieldValue }) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}
            className="form form-label-right"
          >
            <DebtCollectionFilterForm
              intl={intl}
              handleBlur={handleBlur}
              values={values}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              applyFilter={applyFilter}
              franchiseOptions={franchiseOptions}
              showFranchiseFilter={showDebtFranchiseFilter}
              exportBusy={exportBusy}
              onExportSummary={handleExportSummary}
              onExportLedger={handleExportLedger}
            />
          </form>
        )}
      </Formik>
    </>
  );
}
